import React, { useEffect } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { MentionSearchResult } from '../../types/mention';

interface MentionAutocompleteProps {
  searchTerm: string;
  onSelect: (user: MentionSearchResult) => void;
  onClose: () => void;
  results: MentionSearchResult[];
  loading?: boolean;
  selectedIndex: number;
}

const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  searchTerm,
  onSelect,
  onClose,
  results,
  loading = false,
  selectedIndex,
}) => {
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement;
      if (!target.closest('.mention-autocomplete')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!results.length && !loading && searchTerm) {
    return (
      <Paper
        className="mention-autocomplete"
        elevation={3}
        sx={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          mt: 0.5,
          maxHeight: 300,
          zIndex: 1300,
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No users found
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try a different search
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!results.length && !searchTerm) {
    return (
      <Paper
        className="mention-autocomplete"
        elevation={3}
        sx={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          mt: 0.5,
          maxHeight: 300,
          zIndex: 1300,
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Type to search users...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      className="mention-autocomplete"
      elevation={3}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        mt: 0.5,
        maxHeight: 300,
        overflow: 'auto',
        zIndex: 1300,
      }}
    >
      {loading ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <List dense disablePadding>
          {results.map((user, index) => (
            <ListItem key={user.userId} disablePadding>
              <ListItemButton
                selected={index === selectedIndex}
                onClick={() => onSelect(user)}
                sx={{
                  py: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemText-secondary': {
                      color: 'primary.contrastText',
                      opacity: 0.8,
                    },
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={user.photoURL}
                    sx={{ width: 32, height: 32 }}
                  >
                    {user.displayName.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.displayName}
                  secondary={user.personName || user.email}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default MentionAutocomplete;
